import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import {
  Autocomplete,
  Banner,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  EmptyState,
  InlineGrid,
  InlineStack,
  Page,
  Select,
  Tag,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris'
import { DeleteIcon, PlusIcon, XIcon } from '@shopify/polaris-icons'
import { useField, useForm } from '@shopify/react-form'
import ProductImage from '../../../components/ProductImage'

import {
  createRule,
  deleteRule,
  updateRule,
  getRules,
} from '../../../apis/recommendation.js'
import {
  fetchCollectionIdByHandle,
  fetchProductTags,
  fetchProductTypes,
  fetchProductVendors,
} from '../../../apis/shopify.js'
import queryClient from '../../../utils/query.js'
import { RULEBASED_OBJECT_TYPE } from '../../../constants/recommendations.js'
import { getProducts } from '../../../apis/products.js'

export default function SlotRules({
  onSave,
  onCancel,
  initialData = null,
  widgetId = null,
  widgetType = 'slots',
  section = null,
  isInModal = false,
}) {
  const { t } = useTranslation()

  // Error state for component-level error handling
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [slotRules, setSlotRules] = useState(
    initialData ? deserializeSlotRules(initialData) : [],
  )
  const [isLoading, setIsLoading] = useState(false)
  const [pendingChanges, setPendingChanges] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  const [isPopulatingData, setIsPopulatingData] = useState(false)

  // Validation functions
  const validateRule = (rule, ruleIndex) => {
    const errors = {}

    // Validate condition
    if (!rule.conditionType || rule.conditionType.trim() === '') {
      errors[`rule_${ruleIndex}_conditionType`] = t(
        'SlotFBT.validation.conditionTypeRequired',
      )
    }
    if (!rule.conditionSelected || rule.conditionSelected.length === 0) {
      errors[`rule_${ruleIndex}_conditionSelected`] = t(
        'SlotFBT.validation.atLeastOneConditionValueRequired',
      )
    }

    // Validate slots
    if (!rule.slots || rule.slots.length === 0) {
      errors[`rule_${ruleIndex}_slots`] = t(
        'SlotFBT.validation.atLeastOneSlotRequired',
      )
    } else {
      rule.slots.forEach((slot, slotIndex) => {
        if (!slot.rules || slot.rules.length === 0) {
          errors[`rule_${ruleIndex}_slot_${slotIndex}_rules`] = t(
            'SlotFBT.validation.atLeastOneRuleRequiredInSlot',
          )
        } else {
          slot.rules.forEach((slotRule, slotRuleIndex) => {
            if (
              !slotRule.conclusionType ||
              slotRule.conclusionType.trim() === ''
            ) {
              errors[
                `rule_${ruleIndex}_slot_${slotIndex}_rule_${slotRuleIndex}_conclusionType`
              ] = t('SlotFBT.validation.conclusionTypeRequired')
            }
            if (
              !slotRule.conclusionSelected ||
              slotRule.conclusionSelected.length === 0
            ) {
              errors[
                `rule_${ruleIndex}_slot_${slotIndex}_rule_${slotRuleIndex}_conclusionSelected`
              ] = t('SlotFBT.validation.atLeastOneConclusionValueRequired')
            }
          })
        }
      })
    }

    return errors
  }

  const validateAllRules = () => {
    const allErrors = {}
    slotRules.forEach((rule, ruleIndex) => {
      const ruleErrors = validateRule(rule, ruleIndex)
      Object.assign(allErrors, ruleErrors)
    })
    setValidationErrors(allErrors)
    return Object.keys(allErrors).length === 0
  }

  // Function to populate data with full objects for display
  const populateDataWithFullObjects = async (slotRules) => {
    if (!slotRules || !Array.isArray(slotRules)) {
      console.warn('Invalid slotRules data:', slotRules)
      return []
    }

    try {
      const populatedRules = await Promise.all(
        slotRules.map(async (rule) => {
          if (!rule) {
            console.warn('Invalid rule data:', rule)
            return null
          }

          // Create a new rule object to avoid mutations
          let populatedConditionSelected = rule.conditionSelected || []

          // Populate condition data
          if (
            rule.conditionType === RULEBASED_OBJECT_TYPE.PRODUCT &&
            rule.conditionSelected?.length > 0
          ) {
            try {
              const { data } = await getProducts(rule.conditionSelected)
              if (data?.products) {
                populatedConditionSelected = data.products.map((prod) => ({
                  id: prod.id,
                  title: prod.title,
                  images:
                    prod.images?.map((img) => ({
                      originalSrc: img.src,
                      id: img.id,
                    })) || [],
                }))
              }
            } catch (error) {
              console.error('Error populating condition products:', error)
            }
          }

          if (
            rule.conditionType === RULEBASED_OBJECT_TYPE.COLLECTION &&
            rule.conditionSelected?.length > 0
          ) {
            // Collections are already handled as IDs, no need to populate
            populatedConditionSelected = rule.conditionSelected.filter(Boolean)
          }

          // Populate slot rules data
          const populatedSlots = await Promise.all(
            (rule.slots || []).map(async (slot) => {
              if (!slot) return null

              const populatedSlotRules = await Promise.all(
                (slot.rules || []).map(async (slotRule) => {
                  if (!slotRule) return null

                  let populatedConclusionSelected =
                    slotRule.conclusionSelected || []

                  if (
                    slotRule.conclusionType === RULEBASED_OBJECT_TYPE.PRODUCT &&
                    slotRule.conclusionSelected?.length > 0
                  ) {
                    try {
                      const { data } = await getProducts(
                        slotRule.conclusionSelected,
                      )
                      if (data?.products) {
                        populatedConclusionSelected = data.products.map(
                          (prod) => ({
                            id: prod.id,
                            title: prod.title,
                            images:
                              prod.images?.map((img) => ({
                                originalSrc: img.src,
                                id: img.id,
                              })) || [],
                          }),
                        )
                      }
                    } catch (error) {
                      console.error(
                        'Error populating slot rule products:',
                        error,
                      )
                    }
                  }

                  if (
                    slotRule.conclusionType ===
                      RULEBASED_OBJECT_TYPE.COLLECTION &&
                    slotRule.conclusionSelected?.length > 0
                  ) {
                    populatedConclusionSelected =
                      slotRule.conclusionSelected.filter(Boolean)
                  }

                  return {
                    ...slotRule,
                    conclusionSelected: populatedConclusionSelected,
                  }
                }),
              )

              return {
                ...slot,
                rules: populatedSlotRules.filter(Boolean),
              }
            }),
          )

          return {
            ...rule,
            conditionSelected: populatedConditionSelected,
            slots: populatedSlots.filter(Boolean),
          }
        }),
      )

      return populatedRules.filter(Boolean)
    } catch (error) {
      console.error('Error in populateDataWithFullObjects:', error)
      throw error
    }
  }

  // Load existing rules from API using react-query
  const {
    data: rulesData,
    isLoading: isLoadingRules,
    isError: isRulesError,
    refetch: refetchRules,
  } = useQuery({
    queryKey: ['slotRules', widgetType, section?.id || widgetId, 'slot_rule'],
    queryFn: async () => {
      if (!widgetId && !section?.id) {
        return { rules: [] }
      }

      const res = await getRules({
        recommendationType: widgetType,
        section: section?.id || widgetId,
        type: 'slot_rule',
      })

      if (res.error) {
        throw new Error(res.error)
      }

      return res.data || { rules: [] }
    },
    enabled: !!(widgetId || section?.id),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  // Process rules data when it changes
  useEffect(() => {
    let isMounted = true

    const processRulesData = async () => {
      if (!rulesData) return

      if (rulesData.rules && rulesData.rules.length > 0) {
        setIsPopulatingData(true)
        try {
          // Convert API rules to slot rules format
          const apiRules = rulesData.rules

          const convertedRules = apiRules
            .map((rule) => {
              // Convert the rule data to slot rules format
              // Check for the actual API response structure
              if (
                rule.data &&
                (rule.data.slotRules ||
                  (rule.data.slots && rule.data.condition))
              ) {
                // If the data has the old slotRules structure, use deserializeSlotRules
                if (rule.data.slotRules) {
                  const deserializedRules = deserializeSlotRules(rule.data)
                  if (!deserializedRules || !Array.isArray(deserializedRules)) {
                    console.warn(
                      'Invalid deserialized rules data:',
                      deserializedRules,
                    )
                    return null
                  }
                  return deserializedRules.map((slotRule) => ({
                    ...slotRule,
                    dbId: rule.id,
                  }))
                }

                // If the data has the new structure with slots and condition directly
                if (rule.data.slots && rule.data.condition) {
                  const slotRule = {
                    id: rule.data.id || Date.now().toString(),
                    dbId: rule.id,
                    conditionType: rule.data.condition.object_type,
                    conditionSelected: rule.data.condition.object_values || [],
                    conditionVal: rule.data.condition.condition || 'or',
                    slots: rule.data.slots.map((slot, index) => ({
                      id: slot.id || `slot_${index + 1}`,
                      slotNumber: slot.slotNumber || index + 1,
                      rules: slot.rules
                        ? slot.rules.map((slotRule, ruleIndex) => ({
                            id: slotRule.id || `rule_${index}_${ruleIndex}`,
                            conclusionType: slotRule.then.object_type,
                            conclusionSelected:
                              slotRule.then.object_values || [],
                            conclusionVal: slotRule.then.condition || 'or',
                          }))
                        : [],
                    })),
                  }
                  return [slotRule]
                }
              }
              return null
            })
            .filter(Boolean)
            .flat() // Flatten the array since we return arrays

          if (isMounted) {
            // Populate the rules with full objects for display
            const populatedRules =
              await populateDataWithFullObjects(convertedRules)
            setSlotRules(populatedRules)
          }
        } catch (error) {
          console.error('Error processing rules data:', error)
          if (isMounted) {
            setSlotRules([])
          }
        } finally {
          if (isMounted) {
            setIsPopulatingData(false)
          }
        }
      } else if (
        rulesData &&
        (!rulesData.rules || rulesData.rules.length === 0)
      ) {
        // No rules found, set empty array
        if (isMounted) {
          setSlotRules([])
        }
      }
    }

    processRulesData()

    return () => {
      isMounted = false
    }
  }, [rulesData]) // Removed isLoadingRules dependency

  const handleAddRule = () => {
    const newRule = {
      id: Date.now().toString(),
      conditionType: 'tag',
      conditionSelected: [],
      conditionVal: 'or',
      slots: [
        {
          id: Date.now().toString() + '_1',
          slotNumber: 1,
          rules: [
            {
              id: Date.now().toString() + '_rule_1',
              conclusionType: 'product',
              conclusionSelected: [],
              conclusionVal: 'or',
            },
          ],
        },
      ],
    }

    // Add to local state immediately
    setSlotRules([...slotRules, newRule])

    // Track the change for later saving
    setPendingChanges((prev) => [...prev, { type: 'create', rule: newRule }])
  }

  const handleDeleteRule = (ruleIndex) => {
    const rule = slotRules[ruleIndex]

    // Remove from local state immediately
    setSlotRules(slotRules.filter((_, index) => index !== ruleIndex))

    // Track the change for later saving
    if (rule.dbId) {
      setPendingChanges((prev) => [
        ...prev,
        { type: 'delete', ruleId: rule.dbId },
      ])
    } else {
      // If it was a pending create, remove it from pending changes
      setPendingChanges((prev) =>
        prev.filter(
          (change) => !(change.type === 'create' && change.rule.id === rule.id),
        ),
      )
    }
  }

  const handleAddSlot = (ruleIndex) => {
    const rule = slotRules[ruleIndex]
    if (rule.slots.length >= 4) return

    const newSlot = {
      id: Date.now().toString() + '_' + (rule.slots.length + 1),
      slotNumber: rule.slots.length + 1,
      rules: [
        {
          id: Date.now().toString() + '_rule_' + (rule.slots.length + 1),
          conclusionType: 'product',
          conclusionSelected: [],
          conclusionVal: 'or',
        },
      ],
    }

    const updatedRules = [...slotRules]
    updatedRules[ruleIndex].slots.push(newSlot)
    setSlotRules(updatedRules)
  }

  const handleDeleteSlot = (ruleIndex, slotIndex) => {
    const rule = slotRules[ruleIndex]
    // Allow deleting the last slot of each individual rule
    if (slotIndex !== rule.slots.length - 1) return

    const updatedRules = [...slotRules]
    updatedRules[ruleIndex].slots = rule.slots.filter(
      (_, index) => index !== slotIndex,
    )

    // Update slot numbers to maintain sequential numbering
    updatedRules[ruleIndex].slots = updatedRules[ruleIndex].slots.map(
      (slot, index) => ({
        ...slot,
        slotNumber: index + 1,
      }),
    )

    setSlotRules(updatedRules)

    // Track the change for later saving
    if (rule.dbId) {
      setPendingChanges((prev) => [
        ...prev,
        { type: 'update', ruleId: rule.dbId, rule: updatedRules[ruleIndex] },
      ])
    } else {
      // If it's a pending create, update the pending change
      setPendingChanges((prev) =>
        prev.map((change) =>
          change.type === 'create' && change.rule.id === rule.id
            ? { ...change, rule: updatedRules[ruleIndex] }
            : change,
        ),
      )
    }
  }

  const handleUpdateRule = (ruleIndex, updatedRule) => {
    const updatedRules = [...slotRules]
    updatedRules[ruleIndex] = { ...updatedRules[ruleIndex], ...updatedRule }
    setSlotRules(updatedRules)

    // Track the update for later saving
    const rule = updatedRules[ruleIndex]
    if (rule.dbId) {
      setPendingChanges((prev) => [
        ...prev,
        { type: 'update', ruleId: rule.dbId, rule: updatedRules[ruleIndex] },
      ])
    } else {
      // If it's a pending create, update the pending change
      setPendingChanges((prev) =>
        prev.map((change) =>
          change.type === 'create' && change.rule.id === rule.id
            ? { ...change, rule: updatedRules[ruleIndex] }
            : change,
        ),
      )
    }
  }

  const handleUpdateSlot = (ruleIndex, slotIndex, updatedSlot) => {
    const updatedRules = [...slotRules]
    updatedRules[ruleIndex].slots[slotIndex] = {
      ...updatedRules[ruleIndex].slots[slotIndex],
      ...updatedSlot,
    }
    setSlotRules(updatedRules)

    // Track the update for later saving
    const rule = updatedRules[ruleIndex]
    if (rule.dbId) {
      setPendingChanges((prev) => [
        ...prev,
        { type: 'update', ruleId: rule.dbId, rule: updatedRules[ruleIndex] },
      ])
    } else {
      // If it's a pending create, update the pending change
      setPendingChanges((prev) =>
        prev.map((change) =>
          change.type === 'create' && change.rule.id === rule.id
            ? { ...change, rule: updatedRules[ruleIndex] }
            : change,
        ),
      )
    }
  }

  const handleSave = async () => {
    // Validate all rules before saving
    if (!validateAllRules()) {
      shopify.toast.show(t('SlotFBT.errors.pleaseFixValidationErrors'), {
        isError: true,
      })
      return
    }

    if (pendingChanges.length === 0) {
      // No changes to save
      const serializedData = serializeSlotRules(slotRules)
      onSave(serializedData)
      return
    }

    setIsSaving(true)
    try {
      // Process all pending changes
      for (const change of pendingChanges) {
        switch (change.type) {
          case 'create':
            const serializedData = serializeSlotRules([change.rule])
            const payload = {
              recommendationType: widgetType,
              section: section?.id || widgetId,
              type: 'slot_rule',
              data: serializedData.slotRules[0],
            }
            const res = await createRule(payload)
            if (res.error) {
              throw new Error(`Failed to create rule: ${res.error}`)
            }
            // Update the rule with the actual ID from the database
            const createdRule = {
              ...change.rule,
              id: res.data.rule.id,
              dbId: res.data.rule.id,
            }
            setSlotRules((prev) =>
              prev.map((rule) =>
                rule.id === change.rule.id ? createdRule : rule,
              ),
            )
            break

          case 'update':
            const updateSerializedData = serializeSlotRules([change.rule])
            const updatePayload = {
              data: updateSerializedData.slotRules[0],
              section: section?.id || widgetId,
            }
            const updateRes = await updateRule(change.ruleId, updatePayload)
            if (updateRes.error) {
              throw new Error(`Failed to update rule: ${updateRes.error}`)
            }
            break

          case 'delete':
            const deleteRes = await deleteRule(change.ruleId)
            if (deleteRes.error) {
              throw new Error(`Failed to delete rule: ${deleteRes.error}`)
            }
            break
        }
      }

      // Clear pending changes
      setPendingChanges([])

      // Invalidate and refetch rules query
      queryClient.invalidateQueries([
        'slotRules',
        widgetType,
        section?.id || widgetId,
      ])

      // Call parent onSave with final data
      const finalSerializedData = serializeSlotRules(slotRules)
      onSave(finalSerializedData)

      shopify.toast.show(t('SlotFBT.errors.slotRulesSavedSuccessfully'))

      // If in modal context, close the modal after successful save
      if (isInModal) {
        onCancel()
      }
    } catch (error) {
      console.error('Error saving slot rules:', error)
      setHasError(true)
      setErrorMessage(error.message)
      shopify.toast.show(
        t('SlotFBT.errors.failedToSaveSlotRules', { error: error.message }),
        { isError: true },
      )
    } finally {
      setIsSaving(false)
    }

    refetchRules()
  }

  // Error boundary rendering
  if (hasError) {
    return (
      <Box padding="400">
        <Card>
          <Banner
            title={t('SlotFBT.errors.errorLoadingSlotRules')}
            tone="critical"
            action={{
              content: t('SlotFBT.buttons.retry'),
              onAction: () => {
                setHasError(false)
                setErrorMessage('')
                refetchRules()
              },
            }}
          >
            <p>{errorMessage || t('SlotFBT.errors.unableToLoadSlotRules')}</p>
          </Banner>
        </Card>
      </Box>
    )
  }

  return (
    <Box padding="400">
      <BlockStack gap="400">
        {/* Save Button */}
        <InlineStack align="end" gap="200">
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            {t('SlotFBT.buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={
              pendingChanges.length === 0 ||
              isSaving ||
              isLoadingRules ||
              isRulesError ||
              isPopulatingData
            }
          >
            {isSaving
              ? t('SlotFBT.buttons.saving')
              : `${t('SlotFBT.buttons.saveChanges')}${pendingChanges.length > 0 ? ` (${pendingChanges.length})` : ''}`}
          </Button>
        </InlineStack>
        {isLoadingRules || isPopulatingData ? (
          <Card>
            <BlockStack gap="300" align="center">
              <Text>
                {isLoadingRules
                  ? t('SlotFBT.loading.loadingSlotRules')
                  : t('SlotFBT.loading.populatingRuleData')}
              </Text>
            </BlockStack>
          </Card>
        ) : isRulesError ? (
          <Card>
            <Banner
              title={t('SlotFBT.errors.errorLoadingSlotRules')}
              tone="critical"
              action={{
                content: t('SlotFBT.buttons.retry'),
                onAction: refetchRules,
              }}
            >
              <p>{t('SlotFBT.errors.unableToLoadSlotRules')}</p>
            </Banner>
          </Card>
        ) : slotRules.length === 0 ? (
          <Card>
            <EmptyState
              heading={t('SlotFBT.emptyState.noSlotRulesConfigured')}
              action={{
                content: t('SlotFBT.buttons.addRule'),
                onAction: handleAddRule,
              }}
            />
          </Card>
        ) : (
          slotRules.map((rule, ruleIndex) => (
            <SlotRuleForm
              key={rule.id}
              rule={rule}
              ruleIndex={ruleIndex}
              onUpdate={(updatedRule) =>
                handleUpdateRule(ruleIndex, updatedRule)
              }
              onDelete={() => handleDeleteRule(ruleIndex)}
              onAddSlot={() => handleAddSlot(ruleIndex)}
              onUpdateSlot={(slotIndex, updatedSlot) =>
                handleUpdateSlot(ruleIndex, slotIndex, updatedSlot)
              }
              onDeleteSlot={(slotIndex) =>
                handleDeleteSlot(ruleIndex, slotIndex)
              }
              validationErrors={validationErrors}
            />
          ))
        )}

        <Button
          variant="secondary"
          icon={PlusIcon}
          onClick={handleAddRule}
          loading={isLoading || isLoadingRules}
          disabled={isLoading || isLoadingRules || isRulesError || isSaving}
          fullWidth
        >
          {t('SlotFBT.buttons.addNewRule')}
        </Button>
      </BlockStack>
    </Box>
  )
}

function SlotRuleForm({
  rule,
  ruleIndex,
  onUpdate,
  onDelete,
  onAddSlot,
  onUpdateSlot,
  onDeleteSlot,
  isSlotRule = false,
  validationErrors = {},
  slotRuleIndex,
  slotIndex,
  parentRuleIndex,
}) {
  const { t } = useTranslation()

  // If this is a slot rule (conclusion rule), handle it differently
  if (isSlotRule) {
    const formFields = {
      conclusionType: useField(rule.conclusionType),
      conclusionSelected: useField(rule.conclusionSelected),
      conclusionVal: useField(rule.conclusionVal),
    }

    const { fields } = useForm({
      fields: formFields,
      onSubmit: () => {},
    })

    const handleConclusionTypeChange = (value) => {
      fields.conclusionType.onChange(value)
      fields.conclusionSelected.onChange([])
      onUpdate({
        conclusionType: value,
        conclusionSelected: [],
      })
    }

    const handleConclusionSelectedChange = (value) => {
      fields.conclusionSelected.onChange(value)
      onUpdate({
        conclusionSelected: value,
      })
    }

    const handleConclusionValChange = (value) => {
      fields.conclusionVal.onChange(value)
      onUpdate({
        conclusionVal: value,
      })
    }

    const ConclusionInputComponent =
      InputComponentMap[fields.conclusionType.value] || ErrorLoadingComponent
    const ConclusionSelectedComponent =
      SelectedComponentMap[fields.conclusionType.value] || ErrorLoadingComponent

    return (
      <Card padding="300">
        <BlockStack gap="300">
          <Text variant="headingSm" as="h5">
            Recommendation Rule
          </Text>

          <BlockStack gap="200">
            <InlineStack gap="200" align="start" blockAlign="center">
              <Select
                options={[
                  { label: 'Product', value: 'product' },
                  { label: 'Tag', value: 'tag' },
                  { label: 'Product Type', value: 'product_type' },
                  { label: 'Collection', value: 'collection' },
                  { label: 'Vendor', value: 'vendor' },
                ]}
                value={fields.conclusionType.value}
                onChange={handleConclusionTypeChange}
              />
              <Text as="p" fontWeight="bold" style={{ alignSelf: 'center' }}>
                in
              </Text>
              <ConclusionInputComponent
                selected={fields.conclusionSelected.value}
                type={fields.conclusionType.value}
                onAdd={(val) => {
                  const deduplicatedVal = Array.from(
                    new Set([...fields.conclusionSelected.value, val]),
                  )
                  handleConclusionSelectedChange(deduplicatedVal)
                }}
                onSubmit={(vals) => {
                  handleConclusionSelectedChange(vals)
                }}
              />
              <Select
                label="Logic"
                labelInline
                value={fields.conclusionVal.value}
                onChange={handleConclusionValChange}
                options={[
                  { label: 'AND', value: 'and' },
                  { label: 'OR', value: 'or' },
                ]}
              />
            </InlineStack>

            {/* Conclusion Validation Errors */}
            {isSlotRule &&
              validationErrors[
                `rule_${parentRuleIndex}_slot_${slotIndex}_rule_${slotRuleIndex}_conclusionType`
              ] && (
                <Text variant="bodySm" tone="critical">
                  {
                    validationErrors[
                      `rule_${parentRuleIndex}_slot_${slotIndex}_rule_${slotRuleIndex}_conclusionType`
                    ]
                  }
                </Text>
              )}
            {isSlotRule &&
              validationErrors[
                `rule_${parentRuleIndex}_slot_${slotIndex}_rule_${slotRuleIndex}_conclusionSelected`
              ] && (
                <Text variant="bodySm" tone="critical">
                  {
                    validationErrors[
                      `rule_${parentRuleIndex}_slot_${slotIndex}_rule_${slotRuleIndex}_conclusionSelected`
                    ]
                  }
                </Text>
              )}
          </BlockStack>

          {/* Selected Conclusions Display */}
          {fields.conclusionSelected.value?.length > 0 && (
            <ConclusionSelectedComponent
              selected={fields.conclusionSelected.value}
              setSelected={handleConclusionSelectedChange}
            />
          )}
        </BlockStack>
      </Card>
    )
  }

  // Main condition rule form
  const formFields = {
    conditionType: useField(rule.conditionType),
    conditionSelected: useField(rule.conditionSelected),
    conditionVal: useField(rule.conditionVal),
  }

  const { fields } = useForm({
    fields: formFields,
    onSubmit: () => {},
  })

  const handleConditionTypeChange = (value) => {
    fields.conditionType.onChange(value)
    fields.conditionSelected.onChange([])
    onUpdate({
      conditionType: value,
      conditionSelected: [],
    })
  }

  const handleConditionSelectedChange = (value) => {
    fields.conditionSelected.onChange(value)
    onUpdate({
      conditionSelected: value,
    })
  }

  const handleConditionValChange = (value) => {
    fields.conditionVal.onChange(value)
    onUpdate({
      conditionVal: value,
    })
  }

  const ConditionInputComponent =
    InputComponentMap[fields.conditionType.value] || ErrorLoadingComponent
  const ConditionSelectedComponent =
    SelectedComponentMap[fields.conditionType.value] || ErrorLoadingComponent

  return (
    <Card padding="400">
      <BlockStack gap="400">
        {/* Rule Header */}
        <InlineStack align="space-between">
          <Text variant="headingMd" as="h3">
            {t('SlotFBT.labels.rule', { number: ruleIndex + 1 })}
          </Text>
          <Button icon={DeleteIcon} onClick={onDelete} tone="critical" />
        </InlineStack>

        {/* Condition Section */}
        <Card padding="300">
          <BlockStack gap="300">
            <Text variant="headingSm" as="h4">
              {t('SlotFBT.labels.ifCondition')}
            </Text>

            <BlockStack gap="200">
              <InlineStack gap="200" align="start" blockAlign="center">
                <Select
                  options={[
                    { label: t('SlotFBT.options.product'), value: 'product' },
                    { label: t('SlotFBT.options.tag'), value: 'tag' },
                    {
                      label: t('SlotFBT.options.productType'),
                      value: 'product_type',
                    },
                    {
                      label: t('SlotFBT.options.collection'),
                      value: 'collection',
                    },
                    { label: t('SlotFBT.options.vendor'), value: 'vendor' },
                    {
                      label: t('SlotFBT.options.cartPrice'),
                      value: 'cartPrice',
                    },
                  ]}
                  value={fields.conditionType.value}
                  onChange={handleConditionTypeChange}
                />
                <Text as="p" fontWeight="bold" style={{ alignSelf: 'center' }}>
                  {t('SlotFBT.labels.in')}
                </Text>
                <ConditionInputComponent
                  selected={fields.conditionSelected.value}
                  type={fields.conditionType.value}
                  onAdd={(val) => {
                    const deduplicatedVal = Array.from(
                      new Set([...fields.conditionSelected.value, val]),
                    )
                    handleConditionSelectedChange(deduplicatedVal)
                  }}
                  onSubmit={(vals) => {
                    handleConditionSelectedChange(vals)
                  }}
                  onCartPriceAdd={(val) => {
                    handleConditionSelectedChange([val])
                  }}
                  cartPriceSelected={
                    fields.conditionSelected.value?.[0] ?? {
                      condition: 'gt',
                      price: null,
                    }
                  }
                />
                <Select
                  label={t('SlotFBT.labels.logic')}
                  labelInline
                  value={fields.conditionVal.value}
                  onChange={handleConditionValChange}
                  options={[
                    { label: t('SlotFBT.options.and'), value: 'and' },
                    { label: t('SlotFBT.options.or'), value: 'or' },
                  ]}
                />
              </InlineStack>

              {/* Condition Validation Errors */}
              {validationErrors[`rule_${ruleIndex}_conditionType`] && (
                <Text variant="bodySm" tone="critical">
                  {validationErrors[`rule_${ruleIndex}_conditionType`]}
                </Text>
              )}
              {validationErrors[`rule_${ruleIndex}_conditionSelected`] && (
                <Text variant="bodySm" tone="critical">
                  {validationErrors[`rule_${ruleIndex}_conditionSelected`]}
                </Text>
              )}
            </BlockStack>

            {/* Selected Conditions Display */}
            {fields.conditionSelected.value?.length > 0 && (
              <ConditionSelectedComponent
                selected={fields.conditionSelected.value}
                setSelected={handleConditionSelectedChange}
              />
            )}
          </BlockStack>
        </Card>

        {/* Slots Section */}
        <BlockStack gap="300">
          <InlineStack align="space-between">
            <Text variant="headingSm" as="h4">
              {t('SlotFBT.labels.thenShowInSlots')}
            </Text>
            {rule.slots.length < 4 && (
              <Button variant="secondary" icon={PlusIcon} onClick={onAddSlot}>
                {t('SlotFBT.buttons.addSlot')}
              </Button>
            )}
          </InlineStack>

          {rule.slots.length === 0 ? (
            <Text variant="bodySm" tone="critical">
              {t('SlotFBT.validation.atLeastOneSlotRequired')}
            </Text>
          ) : (
            rule.slots.map((slot, slotIndex) => (
              <SlotForm
                key={slot.id}
                slot={slot}
                slotIndex={slotIndex}
                isLastSlot={slotIndex === rule.slots.length - 1}
                onUpdate={(updatedSlot) => onUpdateSlot(slotIndex, updatedSlot)}
                onDelete={() => onDeleteSlot(slotIndex)}
                validationErrors={validationErrors}
                ruleIndex={ruleIndex}
              />
            ))
          )}
        </BlockStack>
      </BlockStack>
    </Card>
  )
}

function SlotForm({
  slot,
  slotIndex,
  isLastSlot,
  onUpdate,
  onDelete,
  validationErrors = {},
  ruleIndex,
}) {
  const { t } = useTranslation()

  const handleUpdateRule = (ruleIndex, updatedRule) => {
    const updatedRules = [...slot.rules]
    updatedRules[ruleIndex] = { ...updatedRules[ruleIndex], ...updatedRule }
    onUpdate({
      ...slot,
      rules: updatedRules,
    })
  }

  return (
    <Card padding="300">
      <BlockStack gap="300">
        <InlineStack align="space-between">
          <Text variant="headingSm" as="h5">
            {t('SlotFBT.labels.slot', { number: slot.slotNumber })}
          </Text>
          {isLastSlot && (
            <Button icon={DeleteIcon} onClick={onDelete} tone="critical" />
          )}
        </InlineStack>

        {slot.rules.length === 0 ? (
          <Text variant="bodySm" tone="critical">
            {t('SlotFBT.validation.atLeastOneRuleRequiredInSlot')}
          </Text>
        ) : (
          slot.rules.map((rule, ruleIndex) => (
            <SlotRuleForm
              key={rule.id}
              rule={rule}
              ruleIndex={ruleIndex}
              onUpdate={(updatedRule) =>
                handleUpdateRule(ruleIndex, updatedRule)
              }
              onDelete={() => {}} // Don't allow deleting the auto-created rule
              onAddSlot={() => {}} // Not applicable for slot rules
              onUpdateSlot={() => {}} // Not applicable for slot rules
              onDeleteSlot={() => {}} // Not applicable for slot rules
              isSlotRule={true}
              validationErrors={validationErrors}
              slotRuleIndex={ruleIndex}
              slotIndex={slotIndex}
              parentRuleIndex={ruleIndex}
            />
          ))
        )}
      </BlockStack>
    </Card>
  )
}

// Input Components (reused from RuleBasedRecommendation)
const InputComponentMap = {
  product: InputProduct,
  tag: InputText,
  collection: InputCollection,
  vendor: InputText,
  product_type: InputText,
  cartPrice: CartPriceInput,
}

const SelectedComponentMap = {
  product: ({ selected, setSelected }) => (
    <ProductList products={selected} setProducts={setSelected} />
  ),
  tag: ({ selected, setSelected }) => (
    <TagsList tags={selected} setTags={setSelected} />
  ),
  collection: ({ selected, setSelected }) => (
    <TagsList tags={selected} setTags={setSelected} />
  ),
  vendor: ({ selected, setSelected }) => (
    <TagsList tags={selected} setTags={setSelected} />
  ),
  product_type: ({ selected, setSelected }) => (
    <TagsList tags={selected} setTags={setSelected} />
  ),
  cartPrice: ({ selected }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--p-space-400)',
      }}
    >
      <Text as="strong">Select cart price values</Text>
    </div>
  ),
}

function InputText({ selected, onAdd, type = '' }) {
  const { t } = useTranslation()
  const [val, setVal] = useState('')
  const [options, setOptions] = useState([])
  const [filteredOptions, setFilteredOptions] = useState([])
  const [suggestion, setSuggestion] = useState('')

  const optionsMap = filteredOptions
    .filter((option) => !selected.includes(option))
    .map((option) => ({
      value: option,
      label: option,
    }))

  const fetchDetails = async () => {
    if (type == 'tag') {
      const { data, error } = await fetchProductTags(true)
      if (!error) {
        setOptions(data)
        setFilteredOptions(data)
      }
    } else if (type == 'vendor') {
      const { data, error } = await fetchProductVendors()
      if (!error) {
        setOptions(data)
        setFilteredOptions(data)
      }
    } else if (type == 'product_type') {
      try {
        const data = await fetchProductTypes()
        setOptions(data)
        setFilteredOptions(data)
      } catch (e) {
        console.log(e)
      }
    }
  }

  useEffect(() => {
    fetchDetails()
  }, [type])

  const handleChange = useCallback(
    (value) => {
      const filteredOptions = value
        ? options.filter((option) =>
            option.toLowerCase().includes(value.toLowerCase()),
          )
        : options
      setFilteredOptions(filteredOptions)
      const suggestion =
        value &&
        filteredOptions.find((suggestion) =>
          suggestion.toLowerCase().startsWith(value.toLowerCase()),
        )
      setSuggestion(suggestion)
      setVal(value)
    },
    [options],
  )

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()

        const trimmedVal = val.trim()

        // Validate the input
        if (trimmedVal.length < 2) {
          return // Don't allow very short values
        }

        // Check if the value already exists in options
        const exists = options.includes(trimmedVal)

        if (!exists) {
          // Add to local options for immediate feedback
          setOptions((prev) => [...prev, trimmedVal])
          setFilteredOptions((prev) => [...prev, trimmedVal])
        }

        onAdd(trimmedVal)
        setVal('')
        setSuggestion('')
      }
    },
    [val, options, onAdd],
  )

  return (
    <InlineStack gap="200">
      <Autocomplete
        options={optionsMap}
        selected={selected}
        onSelect={(selected) => {
          const newVal = selected[0]
          if (newVal) {
            onAdd(newVal)
            setVal('')
          }
        }}
        textField={
          <div
            onKeyDown={(event) => {
              handleKeyDown(event)
            }}
          >
            <Autocomplete.TextField
              value={val}
              onChange={handleChange}
              placeholder="Enter value..."
              suggestion={suggestion}
              autoComplete="off"
            />
          </div>
        }
      />
      <Button
        variant="secondary"
        icon={PlusIcon}
        onClick={() => {
          if (val.length > 0) {
            onAdd(val)
            setVal('')
          }
        }}
      >
        Add
      </Button>
    </InlineStack>
  )
}

function InputCollection({ selected: collections, onSubmit }) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  return (
    <Button
      variant="secondary"
      loading={loading}
      onClick={async () => {
        try {
          setLoading(true)
          const collectionIds = await getCollectionIdsFromHandle(collections)
          setLoading(false)
          const selected = await shopify.resourcePicker({
            type: 'collection',
            selectionIds: collectionIds.map((id) => ({
              id: 'gid://shopify/Collection/' + id,
            })),
            multiple: 20,
          })
          if (Array.isArray(selected)) {
            const handles = selected.map(({ handle }) => handle)
            const mergedHandles = [...collections, ...handles]
            const uniqueHandles = [...new Set(mergedHandles)]
            onSubmit(uniqueHandles)
          }
        } catch (e) {
          window.shopify.toast.show('Error updating products', {
            isError: true,
          })
        }
      }}
    >
      {collections.length > 0
        ? `Selected (${collections.length})`
        : 'Select Collections'}
    </Button>
  )

  async function getCollectionIdsFromHandle(collections) {
    const _collectionIds = await Promise.all(
      collections.map(async (handle) => {
        const res = await fetchCollectionIdByHandle(handle)
        if (res.data) {
          return res.data
        }
      }),
    )
    return _collectionIds
  }
}

function InputProduct({ selected: products, onSubmit }) {
  return (
    <Button
      variant="secondary"
      onClick={async () => {
        try {
          const selected = await shopify.resourcePicker({
            type: 'product',
            selectionIds: products.map(({ id }) => ({
              id: 'gid://shopify/Product/' + id,
            })),
            multiple: 20,
            filter: {
              variants: false,
              archived: false,
            },
          })
          if (Array.isArray(selected)) {
            onSubmit(
              selected.map(({ id, title, images }) => ({
                id: parseInt(id.replace('gid://shopify/Product/', '')),
                title,
                images,
              })),
            )
          }
        } catch (e) {
          window.shopify.toast.show('Error updating products', {
            isError: true,
          })
        }
      }}
    >
      Select Products
    </Button>
  )
}

function CartPriceInput({ onCartPriceAdd, cartPriceSelected }) {
  return (
    <InlineGrid columns={'auto auto'} gap={'200'}>
      <Select
        options={[
          { label: 'Greater than', value: 'gt' },
          { label: 'Less than', value: 'lt' },
        ]}
        value={cartPriceSelected?.condition ?? 'gt'}
        onChange={(val) => {
          onCartPriceAdd({
            condition: val,
            price: cartPriceSelected?.price ?? null,
          })
        }}
      />
      <TextField
        placeholder="Price"
        prefix={window.shopify.data.shop.currency}
        value={cartPriceSelected?.price ?? 0}
        type="number"
        onChange={(val) => {
          onCartPriceAdd({
            condition: cartPriceSelected?.condition ?? 'gt',
            price: isNaN(parseInt(val)) ? 0 : parseInt(val),
          })
        }}
      />
    </InlineGrid>
  )
}

function ProductList({ products, setProducts }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignContent: 'center',
        overflowX: 'auto',
        overflowY: 'clip',
        gap: 'var(--p-space-150)',
      }}
    >
      {products.map((val, index) => {
        // Handle both full objects and ID-only data
        const productId = typeof val === 'object' ? val.id : val
        const productTitle =
          typeof val === 'object' ? val.title : `Product ${productId}`
        const productImage =
          typeof val === 'object' ? val.images?.[0]?.originalSrc : null

        return (
          <div
            key={productId}
            style={{
              position: 'relative',
              display: 'inline-block',
            }}
          >
            <Tooltip content={productTitle}>
              <ProductImage
                src={productImage}
                style={{
                  width: 50,
                  height: 50,
                  backgroundColor: 'var(--p-color-avatar-bg-fill)',
                }}
              />
            </Tooltip>
            {setProducts && (
              <div
                style={{
                  position: 'absolute',
                  top: -8,
                  right: 2,
                  minWidth: '20px',
                  width: '20px',
                  height: '20px',
                  padding: 6,
                  borderRadius: '50%',
                  backgroundColor: 'var(--p-color-surface-critical)',
                  boxShadow: 'var(--p-shadow-200)',
                }}
              >
                <Button
                  icon={XIcon}
                  size="micro"
                  variant="plain"
                  tone="critical"
                  onClick={() => {
                    setProducts(products.filter((_, i) => i !== index))
                  }}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function TagsList({ tags, setTags }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        height: '4rem',
        gap: '5px',
        columnGap: '5px',
        width: 'max-content',
        paddingBlockEnd: '5px',
      }}
    >
      {tags.map((tag, indx) => {
        // Handle both full objects and simple values
        const tagValue =
          typeof tag === 'object' ? tag.value || tag.title || tag.id : tag
        const tagTitle = typeof tag === 'object' ? tag.title : tag

        const tagComponent = (
          <Tag
            onRemove={() => {
              setTags(tags.filter((val, ind) => ind !== indx))
            }}
          >
            {tagValue}
          </Tag>
        )
        return (
          <div
            style={{
              flex: '2',
            }}
            key={indx}
          >
            {typeof tag === 'object' && tag?.title ? (
              <Tooltip content={tagTitle}>{tagComponent}</Tooltip>
            ) : (
              tagComponent
            )}
          </div>
        )
      })}
    </div>
  )
}

function ErrorLoadingComponent() {
  return (
    <Box>
      <Banner
        tone="critical"
        title="There's some error in this particular rule. Kindly contact support and don't forget to attach a screenshot of this rule."
      />
    </Box>
  )
}

// Serialization and Deserialization Functions
export function serializeSlotRules(slotRules) {
  return {
    slotRules: slotRules.map((rule) => ({
      id: rule.id,
      condition: {
        object_type: rule.conditionType,
        object_values: extractIds(rule.conditionSelected, rule.conditionType),
        condition: rule.conditionVal,
      },
      slots: rule.slots.map((slot) => ({
        id: slot.id,
        slotNumber: slot.slotNumber,
        rules: slot.rules.map((slotRule) => ({
          id: slotRule.id,
          then: {
            object_type: slotRule.conclusionType,
            object_values: extractIds(
              slotRule.conclusionSelected,
              slotRule.conclusionType,
            ),
            condition: slotRule.conclusionVal,
          },
        })),
      })),
    })),
  }
}

// Helper function to extract IDs from products and collections
function extractIds(values, type) {
  if (!values || !Array.isArray(values)) return []

  if (type === 'product') {
    return values.map((item) => {
      if (typeof item === 'object' && item.id) {
        return item.id
      }
      // If it's already an ID (number or string), return as is
      return item
    })
  }

  if (type === 'collection') {
    return values.map((item) => {
      if (typeof item === 'object' && item.id) {
        return item.id
      }
      // If it's already an ID (number or string), return as is
      return item
    })
  }

  // For other types (tag, vendor, product_type), return as is
  return values
}

export function deserializeSlotRules(data) {
  if (!data || !data.slotRules) return []

  return data.slotRules.map((rule) => ({
    id: rule.id,
    dbId: rule.id, // Store the database ID for API operations
    conditionType: rule.condition.object_type,
    conditionSelected: rule.condition.object_values || [],
    conditionVal: rule.condition.condition || 'or',
    slots: rule.slots.map((slot) => ({
      id: slot.id,
      slotNumber: slot.slotNumber,
      rules: slot.rules.map((slotRule) => ({
        id: slotRule.id,
        conclusionType: slotRule.then.object_type,
        conclusionSelected: slotRule.then.object_values || [],
        conclusionVal: slotRule.then.condition || 'or',
      })),
    })),
  }))
}
